<template>
  <filter-groupings-expand-toggle v-bind="$attrs" :cache-key="cacheKey" />
  <filter-grouping
    v-if="checkboxFilters && checkboxFilters.bodyfocus_ids"
    title="Body Focus"
    icon="accessibility_new"
    :cache-key="cacheKey"
    :grouping-id="groupingIds.bodyFocusFilterGroup"
    :has-filters="searchStore.hasFilters({ checkbox: ['bodyfocus_ids'] })"
    container-classes="bodyfocus"
  >
    <checkbox-filter
      :checkbox-filter="checkboxFilters.bodyfocus_ids"
      :tri-state="features.VIDEO_FILTERS"
      :toggle-all="true"
      :show-title="false"
      @checkbox-change="triggerFiltering"
    />
  </filter-grouping>
  <filter-grouping
    v-if="rangeFilters && rangeFilters.duration"
    title="Duration"
    icon="timer"
    :cache-key="cacheKey"
    :grouping-id="groupingIds.durationFilterGroup"
    :has-filters="searchStore.hasFilters({ range: ['duration'] })"
    container-classes="duration-minutes"
  >
    <range-filter
      :range-filter="rangeFilters.duration"
      max="100"
      @range-change="triggerFiltering"
    />
  </filter-grouping>
  <filter-grouping
    v-if="checkboxFilters && checkboxFilters.trainingtype_ids"
    title="Training Type"
    icon="directions_run"
    :cache-key="cacheKey"
    :grouping-id="groupingIds.trainingTypeFilterGroup"
    :has-filters="searchStore.hasFilters({ checkbox: ['trainingtype_ids'] })"
    container-classes="training-type"
  >
    <checkbox-filter
      :checkbox-filter="checkboxFilters.trainingtype_ids"
      :tri-state="features.VIDEO_FILTERS"
      :toggle-all="true"
      :show-title="false"
      @checkbox-change="triggerFiltering"
    />
  </filter-grouping>
  <filter-grouping
    v-if="checkboxFilters && checkboxFilters.equipment_ids"
    title="Equipment"
    icon="fitness_center"
    :cache-key="cacheKey"
    :grouping-id="groupingIds.equipmentFilterGroup"
    :has-filters="searchStore.hasFilters({ checkbox: ['equipment_ids'] })"
    container-classes="equipment"
  >
    <checkbox-filter
      :checkbox-filter="checkboxFilters.equipment_ids"
      :tri-state="features.VIDEO_FILTERS"
      :toggle-all="true"
      :show-title="false"
      @checkbox-change="triggerFiltering"
    />
  </filter-grouping>
  <filter-grouping
    v-if="personalTrainers && physicalTherapyDrs && yogaInstructors && psychologists"
    title="Coaches"
    icon="person_search"
    :cache-key="cacheKey"
    :grouping-id="groupingIds.teamMemberFilterGroup"
    :has-filters="searchStore.hasFilters({ checkbox: ['team_member_ids'] })"
    container-classes="team"
  >
    <team-member-filter
      :checkbox-filter="personalTrainers"
      :tri-state="features.VIDEO_FILTERS"
      title="Personal Trainers"
      @checkbox-change="triggerFiltering"
    />
    <team-member-filter
      :checkbox-filter="physicalTherapyDrs"
      :tri-state="features.VIDEO_FILTERS"
      title="Doctors of Physical Therapy"
      @checkbox-change="triggerFiltering"
    />
    <team-member-filter
      :checkbox-filter="yogaInstructors"
      :tri-state="features.VIDEO_FILTERS"
      title="Certified Yoga Instructors"
      @checkbox-change="triggerFiltering"
    />
    <team-member-filter
      :checkbox-filter="psychologists"
      :tri-state="features.VIDEO_FILTERS"
      title="Licensed Psychologists"
      @checkbox-change="triggerFiltering"
    />
  </filter-grouping>
  <filter-grouping
    v-if="checkboxFilters && dropdownFilters && rangeFilters"
    title="Workout Options"
    icon="tune"
    :cache-key="cacheKey"
    :grouping-id="groupingIds.otherFilterGroup"
    :has-filters="
      searchStore.hasFilters({
        exclude: [
          'tags',
          'keywords',
          'bodyfocus_ids',
          'duration',
          'trainingtype_ids',
          'equipment_ids',
          'team_member_ids',
        ],
      })
    "
    container-classes="options"
  >
    <checkbox-filter
      :checkbox-filter="checkboxFilters.is_exclusive"
      :tri-state="features.VIDEO_FILTERS"
      @checkbox-change="triggerFiltering"
    />

    <dropdown-filter
      v-if="features.VIDEO_FILTERS"
      :dropdown-filter="dropdownFilters.history"
      @dropdown-change="triggerFiltering"
    />

    <dropdown-filter
      v-if="features.VIDEO_FILTERS"
      :dropdown-filter="dropdownFilters.includes"
      @dropdown-change="triggerFiltering"
    />

    <range-filter
      :range-filter="rangeFilters.calories"
      max="1500"
      @range-change="triggerFiltering"
    />

    <checkbox-filter
      :checkbox-filter="checkboxFilters.difficulty"
      :tri-state="features.VIDEO_FILTERS"
      @checkbox-change="triggerFiltering"
    />
  </filter-grouping>
</template>

<script setup lang="ts">
/**
 * SIDE EFFECT NOTE
 * If a new exclusive filter is added (v-if="features.VIDEO_FILTERS")
 * then please update video-collections-page exclusiveMsg.  It uses
 * hard coded strings currently
 */
import { computed, onBeforeMount } from 'vue'
import { useSearchFilter } from '@/composables/search-filter'
import { useSearchStore } from '@/stores/search'
import CheckboxFilter from '@/components/search/checkbox-filter.vue'
import FilterGrouping from '@/components/search/filter-grouping.vue'
import DropdownFilter from '@/components/search/dropdown-filter.vue'
import RangeFilter from '@/components/search/range-filter.vue'
import TeamMemberFilter from '@/components/search/team-member-filter.vue'
import FilterGroupingsExpandToggle from '@/components/search/filter-groupings-expand-toggle.vue'

/** STATE **/
const searchStore = useSearchStore()

/** NON REACTIVE **/
// groupingIds and cacheKey use to persist expanded state to local storage
const groupingIds = {
  bodyFocusFilterGroup: 'bodyFocusFilterGroup',
  durationFilterGroup: 'durationFilterGroup',
  trainingTypeFilterGroup: 'trainingTypeFilterGroup',
  equipmentFilterGroup: 'equipmentFilterGroup',
  teamMemberFilterGroup: 'teamMemberFilterGroup',
  otherFilterGroup: 'otherFilterGroup',
}

// local storage cache key for which filters are expanded
const cacheKey = 'video-filter-groupings-expanded-v1'

/** EMITS **/
const emit = defineEmits(['filter-update'])

/** COMPOSABLES **/
const {
  features,
  checkboxFilters,
  rangeFilters,
  dropdownFilters,
  triggerFiltering,
  initFilterGroupingsExpandedCache,
} = useSearchFilter({ emit })

/** LIFECYCLE **/
onBeforeMount(() => {
  // init expanded state from local storage
  initFilterGroupingsExpandedCache({ cacheKey, groupingIds })
})

/** COMPUTED **/
const personalTrainers = computed(() => {
  // manually grouping by ids, no data driven way to do this yet
  if (checkboxFilters.value && checkboxFilters.value.team_member_ids) {
    return {
      ...checkboxFilters.value.team_member_ids,
      items: checkboxFilters.value.team_member_ids.items.filter(
        (item) =>
          // anyone NOT in other categories
          ![8, 14, 15, 19, 22].includes(Number(item.value)),
      ),
    }
  }

  return checkboxFilters.value?.team_member_ids
})

const physicalTherapyDrs = computed(() => {
  // manually grouping by ids, no data driven way to do this yet
  if (checkboxFilters.value && checkboxFilters.value.team_member_ids) {
    return {
      ...checkboxFilters.value.team_member_ids,
      items: checkboxFilters.value.team_member_ids.items.filter((item) =>
        [14, 8].includes(Number(item.value)),
      ),
    }
  }

  return checkboxFilters.value?.team_member_ids
})

const yogaInstructors = computed(() => {
  // manually grouping by ids, no data driven way to do this yet
  if (checkboxFilters.value && checkboxFilters.value.team_member_ids) {
    return {
      ...checkboxFilters.value.team_member_ids,
      items: checkboxFilters.value.team_member_ids.items.filter((item) =>
        [22, 19].includes(Number(item.value)),
      ),
    }
  }

  return checkboxFilters.value?.team_member_ids
})

const psychologists = computed(() => {
  // manually grouping by ids, no data driven way to do this yet
  if (checkboxFilters.value && checkboxFilters.value.team_member_ids) {
    return {
      ...checkboxFilters.value.team_member_ids,
      items: checkboxFilters.value.team_member_ids.items.filter((item) =>
        [15].includes(Number(item.value)),
      ),
    }
  }

  return checkboxFilters.value?.team_member_ids
})
</script>
